// Catalogue converter wizard: digitalize provider catalogue (PDF/Excel/image) → families + products.
// Step 0: form (prompt, file, category, supplier, brand, origin, port). Step 1: preview blocks. Steps 2–6: mapping, converted, match, save, finalize.

import { useState, useRef, useEffect } from 'react';
import { useCategories, useBrands, useOrigins, useOriginPorts, useSuppliers, useLoadingMethods, useProductFamilies, useSourcingItems, useProductStatuses, useMoqUnits, useCostIncoterms } from '@/hooks/use-store';
import { supabase } from '@/lib/supabase';
import {
  useCatalogueConversionDraft,
  useCatalogueConversionDrafts,
  useCatalogueConversionDraftMutation,
  useCreateCatalogueConversionDraft,
} from '@/hooks/use-store';
import { useQueryClient } from '@tanstack/react-query';
import type { RawCatalogueBlock, ColumnMapping, ConvertedBlock, ConvertedProduct, MatchDecisions } from '@/lib/store-types';
import type { ProductFamily, SourcingItem } from '@/lib/store-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { MultiSelect } from '@/components/ui/multi-select';
import { postWorkerApi } from '@/lib/worker-api';
import { fetchResizeAndUpload } from '@/lib/image-utils';

const STEPS = ['0. Config', '1. Preview', '2. Mapping', '3. Converted', '4. Match', '5. Save', '6. Finalize'];

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** Prefix for literal text in column mapping (user-added text, not a provider column). */
const MAPPING_LITERAL_PREFIX = 'literal:';

function applyMapping(row: Record<string, string>, mapping: ColumnMapping): ConvertedProduct {
  const get = (key: string) => {
    const cols = mapping[key];
    if (!cols?.length) return undefined;
    const parts = cols.map((c) =>
      c.startsWith(MAPPING_LITERAL_PREFIX) ? c.slice(MAPPING_LITERAL_PREFIX.length) : (row[c] ?? '')
    );
    return parts.join(' ').trim() || undefined;
  };
  const num = (key: string) => {
    const v = get(key);
    if (v === undefined) return undefined;
    const n = parseFloat(String(v).replace(/,/g, '.'));
    return Number.isNaN(n) ? undefined : n;
  };
  return {
    internal_name: get('internal_name') ?? '',
    sales_format: get('sales_format') ?? '',
    cost_usd: num('cost_usd'),
    units_per_box: num('units_per_box'),
    boxes_per_pallet: num('boxes_per_pallet'),
    boxes_per_container: num('boxes_per_container'),
    gross_weight_per_box_kg: num('gross_weight_per_box_kg'),
    box_length_cm: num('box_length_cm'),
    box_width_cm: num('box_width_cm'),
    box_height_cm: num('box_height_cm'),
    moq_value: num('moq_value'),
    moq_unit_id: undefined,
    observaciones: get('observaciones') ?? undefined,
    internal_notes: get('internal_notes') ?? undefined,
    cost_incoterm: get('cost_incoterm') ?? undefined,
    production_time_days: num('production_time_days'),
    payment_terms: get('payment_terms') ?? undefined,
    referencia: get('referencia') ?? undefined,
    packshot_image: get('packshot_image') ?? undefined,
  };
}

/** Get value for a mapping key from a row (for family-level fields we use first row). */
function getMappedValue(block: RawCatalogueBlock, mapping: ColumnMapping, key: string): string | undefined {
  const cols = mapping[key];
  if (!cols?.length || !block.rows?.length) return undefined;
  const row = block.rows[0];
  const parts = cols.map((c) =>
    c.startsWith(MAPPING_LITERAL_PREFIX) ? c.slice(MAPPING_LITERAL_PREFIX.length) : (row[c] ?? '')
  );
  const v = parts.join(' ').trim();
  return v || undefined;
}

function buildConvertedBlocks(raw_blocks: RawCatalogueBlock[], column_mapping: ColumnMapping | null): ConvertedBlock[] {
  if (!column_mapping || Object.keys(column_mapping).length === 0) return [];
  return raw_blocks.map((block) => ({
    family: {
      family_name: block.family_name || 'Sin nombre',
      slug: slugify(block.family_name || 'sin-nombre'),
      family_description: block.family_description ?? getMappedValue(block, column_mapping, 'family_description'),
    },
    products: (block.rows ?? []).map((row) => applyMapping(row, column_mapping)),
  }));
}

/** Visibility: public = visible to customers in store; private = internal only (admin panel). Aligned with public_sourcing_items view and store-types. */
const VISIBILITY = { public: 'public' as const, private: 'private' as const };

/** Target fields for column mapping. Product + origin-level + family (family_description from first row of block). */
const MAPPING_TARGET_FIELDS: { key: string; label: string; visibility: 'public' | 'private' }[] = [
  { key: 'internal_name', label: 'Internal name (product)', visibility: VISIBILITY.public },
  { key: 'sales_format', label: 'Sales format', visibility: VISIBILITY.public },
  { key: 'cost_usd', label: 'Cost USD', visibility: VISIBILITY.private },
  { key: 'units_per_box', label: 'Units per box', visibility: VISIBILITY.public },
  { key: 'boxes_per_pallet', label: 'Boxes per pallet', visibility: VISIBILITY.public },
  { key: 'boxes_per_container', label: 'Cajas por contenedor', visibility: VISIBILITY.public },
  { key: 'gross_weight_per_box_kg', label: 'Gross weight per box (kg)', visibility: VISIBILITY.public },
  { key: 'box_length_cm', label: 'Largo caja (cm)', visibility: VISIBILITY.public },
  { key: 'box_width_cm', label: 'Ancho caja (cm)', visibility: VISIBILITY.public },
  { key: 'box_height_cm', label: 'Alto caja (cm)', visibility: VISIBILITY.public },
  { key: 'moq_value', label: 'MOQ value', visibility: VISIBILITY.public },
  { key: 'observaciones', label: 'Observaciones', visibility: VISIBILITY.public },
  { key: 'internal_notes', label: 'Info. interna (solo admin)', visibility: VISIBILITY.private },
  { key: 'cost_incoterm', label: 'Cost incoterm (text, e.g. FOB/CIF)', visibility: VISIBILITY.private },
  { key: 'production_time_days', label: 'Production time (days)', visibility: VISIBILITY.public },
  { key: 'payment_terms', label: 'Payment terms', visibility: VISIBILITY.private },
  { key: 'referencia', label: 'Referencia / código proveedor', visibility: VISIBILITY.private },
  { key: 'family_description', label: 'Family description (per family, from first row)', visibility: VISIBILITY.public },
  { key: 'packshot_image', label: 'Link de imagen', visibility: VISIBILITY.public },
];

export default function AdminCatalogueConverter() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draftId, setDraftId] = useState<string | null>(null);

  // Step 0 form state (when no draft or creating new)
  const [userPrompt, setUserPrompt] = useState('');
  const [file, setFile] = useState<{ base64: string; type: 'pdf' | 'excel' | 'image' | 'md' | 'csv' } | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [originId, setOriginId] = useState<string | null>(null);
  const [originPortId, setOriginPortId] = useState<string | null>(null);
  const [loadingMethodId, setLoadingMethodId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const step0SyncedRef = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data: draft, isLoading: draftLoading, isError: draftError, refetch: refetchDraft } = useCatalogueConversionDraft(draftId, { enabled: !!draftId });
  const updateDraft = useCatalogueConversionDraftMutation();
  const createDraft = useCreateCatalogueConversionDraft();

  const { data: categories } = useCategories();
  const { data: brands } = useBrands();
  const { data: origins } = useOrigins();
  const { data: originPorts } = useOriginPorts(originId);
  const { data: suppliers } = useSuppliers();
  const { data: loadingMethods } = useLoadingMethods();
  const { data: productFamilies } = useProductFamilies();
  const { data: sourcingItems } = useSourcingItems(undefined, { admin: true });
  const { data: productStatuses } = useProductStatuses();
  const { data: moqUnits } = useMoqUnits();
  const { data: costIncoterms } = useCostIncoterms();
  const [saving, setSaving] = useState(false);
  const [reversing, setReversing] = useState(false);

  const [digitalizing, setDigitalizing] = useState(false);
  const [suggestMappingLoading, setSuggestMappingLoading] = useState(false);
  const [mappingEdit, setMappingEdit] = useState<ColumnMapping | null>(null);
  const [convertedBlocksEdit, setConvertedBlocksEdit] = useState<ConvertedBlock[] | null>(null);
  const [matchDecisionsEdit, setMatchDecisionsEdit] = useState<MatchDecisions | null>(null);
  /** Per-block mix option family ids (when family decision is "new"). Used at save. */
  const [mixOptionFamilyIdsByBlock, setMixOptionFamilyIdsByBlock] = useState<Record<number, string[]>>({});

  const currentStep = draft?.step ?? 0;

  // When entering step 2, fetch suggested mapping if we don't have column_mapping yet
  useEffect(() => {
    if (currentStep !== 2 || !draftId || !draft?.raw_blocks?.length) return;
    const blocks = draft.raw_blocks as RawCatalogueBlock[];
    const first = blocks[0];
    const raw_headers = first?.headers ?? [];
    if (raw_headers.length === 0) return;
    if (draft.column_mapping && Object.keys(draft.column_mapping).length > 0) {
      setMappingEdit(draft.column_mapping as ColumnMapping);
      return;
    }
    setSuggestMappingLoading(true);
    postWorkerApi<{ mapping?: ColumnMapping }>('/api/catalogue-suggest-mapping', {
      raw_headers,
      raw_sample_rows: (first.rows ?? []).slice(0, 3),
    })
      .then((result) => {
        if (result.ok && result.data.mapping && typeof result.data.mapping === 'object') {
          setMappingEdit(result.data.mapping as ColumnMapping);
        } else if (!result.ok) {
          toast({ title: 'Error', description: result.error ?? 'No se pudo obtener sugerencia de mapeo', variant: 'destructive' });
        }
      })
      .catch(() => toast({ title: 'Error', description: 'No se pudo obtener sugerencia de mapeo', variant: 'destructive' }))
      .finally(() => setSuggestMappingLoading(false));
  }, [currentStep, draftId, draft?.raw_blocks, draft?.column_mapping]);

  // Step 3: derive or load converted_blocks
  useEffect(() => {
    if (currentStep !== 3 || !draft) return;
    const raw_blocks = (draft.raw_blocks ?? []) as RawCatalogueBlock[];
    const column_mapping = draft.column_mapping as ColumnMapping | null;
    if (draft.converted_blocks?.length) {
      setConvertedBlocksEdit(draft.converted_blocks as ConvertedBlock[]);
      return;
    }
    setConvertedBlocksEdit(buildConvertedBlocks(raw_blocks, column_mapping));
  }, [currentStep, draft]);

  // Step 4: init match_decisions from draft or default (all new)
  useEffect(() => {
    if (currentStep !== 4 || !draft) return;
    if (draft.match_decisions?.families?.length) {
      setMatchDecisionsEdit(draft.match_decisions);
      return;
    }
    const blocks = (draft.converted_blocks ?? []) as ConvertedBlock[];
    const families = blocks.map((_, bi) => ({ blockIndex: bi, familyDecision: 'new' as const }));
    const products: { blockIndex: number; rowIndex: number; productDecision: 'new' | string }[] = [];
    blocks.forEach((blk, bi) => {
      blk.products.forEach((_, ri) => {
        products.push({ blockIndex: bi, rowIndex: ri, productDecision: 'new' });
      });
    });
    setMatchDecisionsEdit({ families, products });
  }, [currentStep, draft]);

  // Reset step0 sync when switching to a different draft so form re-fills from new draft
  const prevDraftIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (draftId !== prevDraftIdRef.current) {
      prevDraftIdRef.current = draftId;
      step0SyncedRef.current = false;
    }
  }, [draftId]);

  // Step 0: when draft is loaded with step === 0, pre-fill form from draft once
  useEffect(() => {
    if (!draftId) {
      step0SyncedRef.current = false;
      return;
    }
    if (draft?.step === 0 && !step0SyncedRef.current) {
      setUserPrompt(draft.user_prompt ?? '');
      setCategoryId(draft.category_id ?? null);
      setSupplierId(draft.supplier_id ?? null);
      setBrandId(draft.brand_id ?? null);
      setOriginId(draft.origin_id ?? null);
      setOriginPortId(draft.origin_port_id ?? null);
      setLoadingMethodId(draft.loading_method_id ?? null);
      if (draft.file_base64 && draft.file_type) {
        setFile({ base64: draft.file_base64, type: draft.file_type as 'pdf' | 'excel' | 'image' | 'md' | 'csv' });
      }
      step0SyncedRef.current = true;
    }
  }, [draftId, draft?.step, draft?.user_prompt, draft?.category_id, draft?.supplier_id, draft?.brand_id, draft?.origin_id, draft?.origin_port_id, draft?.loading_method_id, draft?.file_base64, draft?.file_type]);

  // Cancel polling when leaving the page so we don't run setState/toast after unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current != null) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = undefined;
      }
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    const name = f.name.toLowerCase();
    let type: 'pdf' | 'excel' | 'image' | 'md' | 'csv' = 'image';
    if (name.endsWith('.pdf')) type = 'pdf';
    else if (name.endsWith('.xlsx') || name.endsWith('.xls')) type = 'excel';
    else if (name.endsWith('.md')) type = 'md';
    else if (name.endsWith('.csv')) type = 'csv';
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = typeof reader.result === 'string' ? reader.result.split(',')[1] : '';
      if (b64) setFile({ base64: b64, type });
      else setFile(null);
    };
    reader.readAsDataURL(f);
  };

  // File to use for digitalize: local file or draft's saved file when in step 0
  const fileToUse =
    file ??
    (draft?.file_base64 && draft?.file_type
      ? { base64: draft.file_base64, type: draft.file_type as 'pdf' | 'excel' | 'image' | 'md' | 'csv' }
      : null);

  const handleStartDigitalize = async () => {
    if (!fileToUse) {
      toast({ title: 'Selecciona un archivo', variant: 'destructive' });
      return;
    }
    setDigitalizing(true);
    let digitalizeResult: { ok: true; data: { raw_blocks?: RawCatalogueBlock[] }; status: number } | { ok: false; status: number; error?: string } | undefined;
    try {
      let id = draftId;
      if (!id) {
        const created = await createDraft.mutateAsync();
        id = created.id;
        setDraftId(id);
      }
      // Save config + file to draft so the worker can load it (async path avoids 524 timeout)
      await updateDraft.mutateAsync({
        id,
        patch: {
          step: 0,
          user_prompt: userPrompt || null,
          category_id: categoryId,
          supplier_id: supplierId,
          brand_id: brandId,
          origin_id: originId,
          origin_port_id: originPortId,
          loading_method_id: loadingMethodId,
          file_type: fileToUse.type,
          file_base64: fileToUse.base64,
        },
      });

      // Send file in body so the worker uses sync path. In production, async (waitUntil) is time-limited and often never completes.
      digitalizeResult = await postWorkerApi<{ raw_blocks?: RawCatalogueBlock[] }>('/api/catalogue-digitalize', {
        draft_id: id,
        user_prompt: userPrompt || undefined,
        file_base64: fileToUse.base64,
        file_type: fileToUse.type,
      });

      if (digitalizeResult.ok && digitalizeResult.status === 202) {
        toast({
          title: 'Digitalizando en segundo plano',
          description: 'Puede tardar 1–2 minutos. Si sales de la pantalla, al volver a cargar el borrador verás el resultado.',
        });
        const deadline = Date.now() + 5 * 60 * 1000;
        let timeoutId: ReturnType<typeof setTimeout>;
        const poll = async (): Promise<void> => {
          if (Date.now() > deadline) {
            pollTimeoutRef.current = undefined;
            setDigitalizing(false);
            toast({ title: 'Tiempo agotado', description: 'La digitalización sigue en curso. Recarga o vuelve a cargar el borrador en un momento.', variant: 'destructive' });
            return;
          }
          const { data: d } = await refetchDraft();
          if (d?.step === 1 && Array.isArray(d?.raw_blocks)) {
            pollTimeoutRef.current = undefined;
            setDigitalizing(false);
            toast({ title: 'Catálogo digitalizado', description: `${d.raw_blocks.length} bloque(s) por familia.` });
            return;
          }
          timeoutId = setTimeout(poll, 3000);
          pollTimeoutRef.current = timeoutId;
        };
        timeoutId = setTimeout(poll, 3000);
        pollTimeoutRef.current = timeoutId;
        return;
      }

      if (!digitalizeResult.ok) throw new Error(digitalizeResult.error || `HTTP ${digitalizeResult.status}`);
      const raw_blocks = (digitalizeResult.data.raw_blocks ?? []) as RawCatalogueBlock[];
      await updateDraft.mutateAsync({
        id,
        patch: {
          step: 1,
          user_prompt: userPrompt || null,
          category_id: categoryId,
          supplier_id: supplierId,
          brand_id: brandId,
          origin_id: originId,
          origin_port_id: originPortId,
          loading_method_id: loadingMethodId,
          file_type: fileToUse.type,
          file_base64: fileToUse.base64,
          raw_blocks,
        },
      });
      toast({ title: 'Catálogo digitalizado', description: `${raw_blocks.length} bloque(s) por familia.` });
    } catch (e) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'Error al digitalizar',
        variant: 'destructive',
      });
      setDigitalizing(false);
    } finally {
      // 202 = async accepted; digitalizing is cleared when poll completes or times out
      if (digitalizeResult?.status !== 202) setDigitalizing(false);
    }
  };

  /** Save step 0 config (params, prompt, file) to draft without digitalizing. Creates draft if needed. */
  const handleSaveConfigStep0 = async () => {
    const base64 = file?.base64 ?? draft?.file_base64 ?? null;
    const fileType = file?.type ?? draft?.file_type ?? null;
    if (!base64 || !fileType) {
      toast({ title: 'Añade un archivo para guardar la configuración', variant: 'destructive' });
      return;
    }
    try {
      let id = draftId;
      if (!id) {
        const created = await createDraft.mutateAsync();
        id = created.id;
        setDraftId(id);
      }
      await updateDraft.mutateAsync({
        id,
        patch: {
          step: 0,
          user_prompt: userPrompt || null,
          category_id: categoryId,
          supplier_id: supplierId,
          brand_id: brandId,
          origin_id: originId,
          origin_port_id: originPortId,
          loading_method_id: loadingMethodId,
          file_type: fileType,
          file_base64: base64,
        },
      });
      toast({ title: 'Configuración guardada', description: 'Puedes continuar después con Start / Digitalize.' });
    } catch (e) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'Error al guardar',
        variant: 'destructive',
      });
    }
  };

  const handleSaveDraft = async () => {
    if (!draftId || !draft) return;
    const patch: { step: number; column_mapping?: ColumnMapping; converted_blocks?: ConvertedBlock[] } = { step: currentStep };
    if (currentStep === 2 && mappingEdit) patch.column_mapping = mappingEdit;
    if (currentStep === 3 && convertedBlocksEdit) patch.converted_blocks = convertedBlocksEdit;
    if (currentStep === 4 && matchDecisionsEdit) (patch as Record<string, unknown>).match_decisions = matchDecisionsEdit;
    await updateDraft.mutateAsync({ id: draftId, patch });
    toast({ title: 'Borrador guardado' });
  };

  const handleNext = async () => {
    if (!draftId || !draft) return;
    const patch: { step: number; column_mapping?: ColumnMapping; converted_blocks?: ConvertedBlock[] } = {
      step: Math.min(currentStep + 1, 6),
    };
    if (currentStep === 2 && mappingEdit) patch.column_mapping = mappingEdit;
    if (currentStep === 3 && convertedBlocksEdit) patch.converted_blocks = convertedBlocksEdit;
    if (currentStep === 4 && matchDecisionsEdit) (patch as Record<string, unknown>).match_decisions = matchDecisionsEdit;
    await updateDraft.mutateAsync({ id: draftId, patch });
    toast({ title: 'Paso guardado', description: 'El borrador se ha guardado automáticamente.' });
  };

  const handleBack = async () => {
    if (!draftId || !draft) return;
    const prev = Math.max(currentStep - 1, 0);
    await updateDraft.mutateAsync({ id: draftId, patch: { step: prev } });
  };

  const publishedStatusId = productStatuses?.find((s) => s.name === 'Published')?.id;

  const handleSaveToDb = async () => {
    if (!draftId || !draft || !publishedStatusId || !draft.supplier_id || !draft.origin_id) {
      toast({ title: 'Faltan configuraciones', description: 'Category, Supplier, Origin obligatorios.', variant: 'destructive' });
      return;
    }
    const blocks = (draft.converted_blocks ?? []) as ConvertedBlock[];
    const decisions = draft.match_decisions as MatchDecisions | null;
    if (!decisions) {
      toast({ title: 'Completa Step 4 (Match)', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const savedFamilyIds: string[] = [];
    const savedItemIds: string[] = [];
    const createdOriginIds: string[] = [];
    try {
      const categoryId = draft.category_id!;
      const familyIdByBlock: Record<number, string> = {};
      for (let bi = 0; bi < blocks.length; bi++) {
        const famDec = decisions.families.find((f) => f.blockIndex === bi);
        const familyId = famDec?.familyDecision === 'new' ? null : (famDec?.familyDecision ?? null);
        if (familyId) {
          familyIdByBlock[bi] = familyId;
        } else {
          const block = blocks[bi];
          const { data: newFamily, error: eF } = await supabase
            .from('product_families')
            .insert({
              family_name: block.family.family_name,
              slug: `${slugify(block.family.family_name)}-${Date.now().toString(36)}`,
              status_id: publishedStatusId,
              marketing_description: block.family.family_description ?? draft.family_description ?? null,
            })
            .select('id')
            .single();
          if (eF) throw eF;
          familyIdByBlock[bi] = newFamily.id;
          savedFamilyIds.push(newFamily.id);
          await supabase.from('product_family_categories').insert({ family_id: newFamily.id, category_id: categoryId });
          const mixOptionIds = mixOptionFamilyIdsByBlock[bi] ?? [];
          for (const relatedId of mixOptionIds) {
            if (relatedId === newFamily.id) continue;
            const { error: mErr } = await supabase.from('product_family_mix_options').insert({ family_id: newFamily.id, related_family_id: relatedId });
            if (mErr) throw mErr;
          }
        }
      }
      for (let bi = 0; bi < blocks.length; bi++) {
        const familyId = familyIdByBlock[bi];
        const block = blocks[bi];
        for (let ri = 0; ri < block.products.length; ri++) {
          const prodDec = decisions.products.find((p) => p.blockIndex === bi && p.rowIndex === ri);
          const product = block.products[ri];
          const itemId = prodDec?.productDecision === 'new' ? null : (prodDec?.productDecision ?? null);
          if (itemId) {
            const { data: insOrigin } = await supabase
              .from('sourcing_item_origins')
              .insert({
                sourcing_item_id: itemId,
                origin_id: draft.origin_id,
                origin_port_id: draft.origin_port_id ?? null,
                supplier_id: draft.supplier_id,
                loading_method_id: draft.loading_method_id ?? null,
                cost_incoterm_id: product.cost_incoterm_id ?? null,
                cost_usd: product.cost_usd ?? null,
                units_per_box: product.units_per_box ?? 0,
                boxes_per_pallet: product.boxes_per_pallet ?? null,
                boxes_per_container: product.boxes_per_container ?? 0,
                gross_weight_per_box_kg: product.gross_weight_per_box_kg ?? 0,
                box_length_cm: product.box_length_cm ?? null,
                box_width_cm: product.box_width_cm ?? null,
                box_height_cm: product.box_height_cm ?? null,
                moq_value: product.moq_value ?? 0,
                moq_unit_id: product.moq_unit_id ?? null,
                production_time_days: product.production_time_days ?? null,
                payment_terms: product.payment_terms ?? null,
                referencia: product.referencia ?? null,
                observaciones: product.observaciones ?? null,
                internal_notes: product.internal_notes ?? null,
                is_active: true,
              })
              .select('id')
              .single();
            if (insOrigin?.id) createdOriginIds.push(insOrigin.id);
            continue;
          }
          const slug = `${slugify(product.internal_name || 'item')}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
          const { data: newItem, error: eI } = await supabase
            .from('sourcing_items')
            .insert({
              internal_name: product.internal_name || 'Sin nombre',
              slug,
              brand_id: draft.brand_id ?? null,
              sales_format: product.sales_format || '',
              status_id: publishedStatusId,
            })
            .select('id')
            .single();
          if (eI) throw eI;
          savedItemIds.push(newItem.id);
          await supabase.from('sourcing_item_families').insert({ item_id: newItem.id, family_id: familyId });
          const { data: insOrigin } = await supabase
            .from('sourcing_item_origins')
            .insert({
              sourcing_item_id: newItem.id,
              origin_id: draft.origin_id,
              origin_port_id: draft.origin_port_id ?? null,
              supplier_id: draft.supplier_id,
              loading_method_id: draft.loading_method_id ?? null,
              cost_incoterm_id: product.cost_incoterm_id ?? null,
              cost_usd: product.cost_usd ?? null,
              units_per_box: product.units_per_box ?? 0,
              boxes_per_pallet: product.boxes_per_pallet ?? null,
              boxes_per_container: product.boxes_per_container ?? 0,
              gross_weight_per_box_kg: product.gross_weight_per_box_kg ?? 0,
              box_length_cm: product.box_length_cm ?? null,
              box_width_cm: product.box_width_cm ?? null,
              box_height_cm: product.box_height_cm ?? null,
              moq_value: product.moq_value ?? 0,
              moq_unit_id: product.moq_unit_id ?? null,
              production_time_days: product.production_time_days ?? null,
              payment_terms: product.payment_terms ?? null,
              referencia: product.referencia ?? null,
              observaciones: product.observaciones ?? null,
              internal_notes: product.internal_notes ?? null,
              is_active: true,
            })
            .select('id')
            .single();
          if (insOrigin?.id) createdOriginIds.push(insOrigin.id);
          // Link de imagen: descargar, subir a Storage y generar thumbnail (mismo flujo que edit product).
          const imageUrl = product.packshot_image?.trim();
          if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
            try {
              const publicUrl = await fetchResizeAndUpload(imageUrl, 'items');
              await supabase.from('sourcing_items').update({ packshot_image: publicUrl }).eq('id', newItem.id);
              await postWorkerApi<{ ok?: boolean }>('/api/sync-item-thumbnail', { itemId: newItem.id });
            } catch (imgErr) {
              toast({
                title: 'Imagen no aplicada',
                description: `${product.internal_name || 'Producto'}: ${imgErr instanceof Error ? imgErr.message : 'No se pudo descargar/subir la imagen.'}`,
                variant: 'destructive',
              });
            }
          }
        }
      }
      if (createdOriginIds.length > 0) {
        const normRes = await postWorkerApi<{ results?: Array<{ origin_id: string; success: boolean; error?: string }>; message?: string }>(
          '/api/normalize-origin-load-data',
          { origin_ids: createdOriginIds, background: true }
        );
        if (normRes.status === 202) {
          toast({ title: 'Datos logísticos en segundo plano', description: normRes.data?.message ?? 'La IA completará los orígenes en unos segundos. Puedes seguir trabajando.' });
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
            queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins'] });
          }, 15000);
        } else {
          const ok = normRes.data?.results?.filter((r) => r.success).length ?? 0;
          if (ok > 0) toast({ title: 'Datos logísticos completados', description: `IA completó ${ok} origen(es)` });
          queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
          queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins'] });
        }
      }
      // Enqueue pSEO generation for newly created items
      if (savedItemIds.length > 0) {
        const seoRes = await postWorkerApi<{ message?: string; queued?: number }>(
          '/api/generate-seo-metadata-batch',
          { ids: savedItemIds, background: true }
        );
        if (seoRes.status === 202) {
          toast({ title: 'pSEO en segundo plano', description: seoRes.data?.message ?? 'La IA generará los metadatos SEO en unos segundos.' });
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
          }, 20000);
        }
      }
      await updateDraft.mutateAsync({
        id: draftId,
        patch: {
          saved_family_ids: [...(draft.saved_family_ids ?? []), ...savedFamilyIds],
          saved_item_ids: [...(draft.saved_item_ids ?? []), ...savedItemIds],
        },
      });
      toast({ title: 'Guardado en DB', description: `${savedFamilyIds.length} familia(s), ${savedItemIds.length} ítem(s).` });
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRollback = async () => {
    if (!draftId || !draft) return;
    const savedItemIds = draft.saved_item_ids ?? [];
    const savedFamilyIds = draft.saved_family_ids ?? [];
    if (savedItemIds.length === 0 && savedFamilyIds.length === 0) {
      toast({ title: 'Nada que revertir', variant: 'destructive' });
      return;
    }
    if (!window.confirm('¿Eliminar los registros creados por esta conversión? (orden: origins → junctions → items → families)')) return;
    setReversing(true);
    try {
      for (const itemId of savedItemIds) {
        await postWorkerApi<{ ok?: boolean }>('/api/delete-product-storage-images', { itemId });
        const { data: origins } = await supabase.from('sourcing_item_origins').select('id').eq('sourcing_item_id', itemId);
        for (const o of origins ?? []) {
          await supabase.from('sourcing_item_origins').delete().eq('id', o.id);
        }
        await supabase.from('sourcing_item_families').delete().eq('item_id', itemId);
        await supabase.from('sourcing_items').delete().eq('id', itemId);
      }
      for (const familyId of savedFamilyIds) {
        await supabase.from('product_family_categories').delete().eq('family_id', familyId);
        await supabase.from('product_families').delete().eq('id', familyId);
      }
      await updateDraft.mutateAsync({
        id: draftId,
        patch: { saved_family_ids: [], saved_item_ids: [] },
      });
      toast({ title: 'Revertido', description: 'Registros creados eliminados.' });
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al revertir', variant: 'destructive' });
    } finally {
      setReversing(false);
    }
  };

  const handleFinalize = async () => {
    if (!draftId) return;
    await updateDraft.mutateAsync({ id: draftId, patch: { finalized_at: new Date().toISOString() } });
    toast({ title: 'Conversión finalizada' });
  };

  const handleRemoveTemporalData = async () => {
    if (!draftId || !window.confirm('¿Borrar datos temporales (raw_blocks, mapping, converted_blocks, match_decisions)? El borrador seguirá existiendo.')) return;
    await updateDraft.mutateAsync({
      id: draftId,
      patch: {
        raw_blocks: null,
        column_mapping: null,
        converted_blocks: null,
        match_decisions: null,
      },
    });
    toast({ title: 'Datos temporales eliminados' });
  };

  const handleClose = () => {
    setDraftId(null);
    navigate('/store/admin');
  };

  // Step 0 form: show when no draft yet or when draft is at step 0 (config saved, not yet digitalized)
  const showStep0Form = !draftId || (draft && draft.step === 0);

  if (draftId && draftLoading) {
    return <div className="p-6">Loading draft…</div>;
  }
  if (draftId && draftError) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-destructive">No se pudo cargar el borrador.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetchDraft()}>Reintentar</Button>
          <Button variant="ghost" onClick={() => setDraftId(null)}>Volver al inicio</Button>
        </div>
      </div>
    );
  }
  if (draftId && !draft) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-muted-foreground">Borrador no encontrado.</p>
        <Button variant="outline" onClick={() => setDraftId(null)}>Volver al inicio</Button>
      </div>
    );
  }

  if (showStep0Form) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Catalogue converter</h1>
        <p className="text-muted-foreground">Digitaliza un catálogo de proveedor (PDF, Excel, CSV, imagen o Markdown) y conviértelo en familias y productos.</p>

        <Card>
          <CardHeader>
            <CardTitle>Paso 0 — Configuración</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Contexto (opcional)</Label>
              <Textarea
                placeholder="Ej: Catálogo FOB Buenos Aires, precios en USD..."
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Archivo (PDF, Excel, CSV, imagen o Markdown .md) *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.xlsx,.xls,.csv,image/*,.md"
                onChange={handleFileChange}
                className="mt-1 block w-full text-sm"
              />
              {(file || (draft?.file_base64 && draft?.file_type)) && (
                <p className="text-sm text-muted-foreground mt-1">
                  {file ? `Listo: ${file.type}` : 'Archivo guardado en borrador'}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={categoryId ?? ''} onValueChange={(v) => setCategoryId(v || null)}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {categories?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <a href="/store/admin/categories" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                  Create new <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div>
                <Label>Supplier (Provider)</Label>
                <Select value={supplierId ?? ''} onValueChange={(v) => setSupplierId(v || null)}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {suppliers?.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <a href="/store/admin/suppliers" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                  Create new <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div>
                <Label>Brand</Label>
                <Select value={brandId ?? ''} onValueChange={(v) => setBrandId(v || null)}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {brands?.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <a href="/store/admin/brands" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                  Create new <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div>
                <Label>Origin (país de origen)</Label>
                <Select value={originId ?? ''} onValueChange={(v) => { setOriginId(v || null); setOriginPortId(null); }}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {origins?.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <a href="/store/admin/origins" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                  Create new <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div>
                <Label>Origin port (puerto de origen)</Label>
                <Select value={originPortId ?? ''} onValueChange={(v) => setOriginPortId(v || null)} disabled={!originId}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {originPorts?.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <a href="/store/admin/origin-ports" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                  Create new <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div>
                <Label>Loading method</Label>
                <Select value={loadingMethodId ?? ''} onValueChange={(v) => setLoadingMethodId(v || null)}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {loadingMethods?.map((lm) => (
                      <SelectItem key={lm.id} value={lm.id}>{lm.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <a href="/store/admin/loading-methods" target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1">
                  Create new <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleSaveConfigStep0}
                disabled={!(file?.base64 ?? draft?.file_base64) || !(file?.type ?? draft?.file_type)}
              >
                Guardar configuración
              </Button>
              <Button onClick={handleStartDigitalize} disabled={digitalizing || !fileToUse}>
                {digitalizing ? 'Digitalizando…' : 'Start / Digitalize'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Optional: list drafts to resume */}
        <DraftList onResume={(id) => setDraftId(id)} />
      </div>
    );
  }

  const raw_blocks = (draft.raw_blocks ?? []) as RawCatalogueBlock[];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Catalogue converter</h1>
      <Stepper current={currentStep} />
      <div className="flex gap-2">
        <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}>Back</Button>
        <Button onClick={handleSaveDraft}>Save draft</Button>
        <Button onClick={handleNext} disabled={currentStep >= 6}>Next</Button>
      </div>

      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 1 — Preview (blocks)</CardTitle>
            <p className="text-sm text-muted-foreground">One table per family from the AI. No grouping in frontend.</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {raw_blocks.length === 0 ? (
              <p className="text-muted-foreground">No blocks. Go back and digitalize again.</p>
            ) : (
              raw_blocks.map((block, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <CardTitle>{block.family_name || `Block ${idx + 1}`}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {block.headers?.map((h, i) => (
                            <TableHead key={i}>{h}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(block.rows ?? []).map((row, ri) => (
                          <TableRow key={ri}>
                            {block.headers?.map((h) => (
                              <TableCell key={h}>{row[h] ?? ''}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {currentStep === 2 && (
        <Step2Mapping
          rawHeaders={raw_blocks[0]?.headers ?? []}
          sampleRow={raw_blocks[0]?.rows?.[0] ?? null}
          mappingEdit={mappingEdit}
          setMappingEdit={setMappingEdit}
          loading={suggestMappingLoading}
          onAcceptAll={() => {
            if (mappingEdit) return;
            setSuggestMappingLoading(true);
            postWorkerApi<{ mapping?: ColumnMapping }>('/api/catalogue-suggest-mapping', {
              raw_headers: raw_blocks[0]?.headers ?? [],
              raw_sample_rows: (raw_blocks[0]?.rows ?? []).slice(0, 3),
            })
              .then((result) => result.ok && result.data.mapping && setMappingEdit(result.data.mapping as ColumnMapping))
              .finally(() => setSuggestMappingLoading(false));
          }}
        />
      )}
      {currentStep === 3 && (
        <Step3Converted
          blocks={convertedBlocksEdit ?? []}
          setBlocks={setConvertedBlocksEdit}
          moqUnits={moqUnits ?? []}
          costIncoterms={costIncoterms ?? []}
        />
      )}
      {currentStep === 4 && (
        <Step4Match
          blocks={convertedBlocksEdit ?? []}
          matchDecisions={matchDecisionsEdit}
          setMatchDecisions={setMatchDecisionsEdit}
          productFamilies={productFamilies ?? []}
          sourcingItems={sourcingItems ?? []}
          mixOptionFamilyIdsByBlock={mixOptionFamilyIdsByBlock}
          setMixOptionFamilyIdsByBlock={setMixOptionFamilyIdsByBlock}
        />
      )}
      {currentStep === 5 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 5 — Save to DB</CardTitle>
            <p className="text-sm text-muted-foreground">
              Create new families and products (status = Published). Rollback removes only records created by this conversion.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              Saved: {(draft.saved_family_ids ?? []).length} family(ies), {(draft.saved_item_ids ?? []).length} item(s).
            </p>
            <div className="flex gap-2">
              <Button onClick={handleSaveToDb} disabled={saving || !publishedStatusId || !draft.supplier_id || !draft.origin_id}>
                {saving ? 'Saving…' : 'Save to DB'}
              </Button>
              <Button variant="outline" onClick={handleRollback} disabled={reversing || ((draft.saved_item_ids?.length ?? 0) === 0 && (draft.saved_family_ids?.length ?? 0) === 0)}>
                {reversing ? 'Reverting…' : 'Reverse'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {currentStep === 6 && (
        <Card>
          <CardHeader>
            <CardTitle>Step 6 — Finalize</CardTitle>
            <p className="text-sm text-muted-foreground">Summary and close.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              Bloques: {(draft.converted_blocks ?? []).length}. Familias guardadas: {(draft.saved_family_ids ?? []).length}. Ítems guardados: {(draft.saved_item_ids ?? []).length}.
            </p>
            {draft.finalized_at ? (
              <p className="text-muted-foreground">Finalizado el {new Date(draft.finalized_at).toLocaleString()}.</p>
            ) : (
              <Button onClick={handleFinalize}>Finalize</Button>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleRemoveTemporalData}>
                Remove temporal data
              </Button>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stepper({ current }: { current: number }) {
  return (
    <nav className="flex items-center gap-2 flex-wrap text-sm">
      {STEPS.map((label, i) => (
        <span
          key={i}
          className={i === current ? 'font-medium' : 'text-muted-foreground'}
        >
          {i === current ? `[${label}]` : label}
        </span>
      ))}
    </nav>
  );
}

function Step4Match({
  blocks,
  matchDecisions,
  setMatchDecisions,
  productFamilies,
  sourcingItems,
  mixOptionFamilyIdsByBlock,
  setMixOptionFamilyIdsByBlock,
}: {
  blocks: ConvertedBlock[];
  matchDecisions: MatchDecisions | null;
  setMatchDecisions: (m: MatchDecisions) => void;
  productFamilies: ProductFamily[];
  sourcingItems: SourcingItem[];
  mixOptionFamilyIdsByBlock: Record<number, string[]>;
  setMixOptionFamilyIdsByBlock: React.Dispatch<React.SetStateAction<Record<number, string[]>>>;
}) {
  if (!matchDecisions) return <p className="text-muted-foreground">Loading match decisions…</p>;

  const setFamilyDecision = (blockIndex: number, value: 'new' | string) => {
    setMatchDecisions({
      ...matchDecisions,
      families: matchDecisions.families.map((f) =>
        f.blockIndex === blockIndex ? { ...f, familyDecision: value } : f
      ),
    });
  };
  const setProductDecision = (blockIndex: number, rowIndex: number, value: 'new' | string) => {
    setMatchDecisions({
      ...matchDecisions,
      products: matchDecisions.products.map((p) =>
        p.blockIndex === blockIndex && p.rowIndex === rowIndex ? { ...p, productDecision: value } : p
      ),
    });
  };
  const getFamilyDecision = (blockIndex: number) =>
    matchDecisions.families.find((f) => f.blockIndex === blockIndex)?.familyDecision ?? 'new';
  const getProductDecision = (blockIndex: number, rowIndex: number) =>
    matchDecisions.products.find((p) => p.blockIndex === blockIndex && p.rowIndex === rowIndex)?.productDecision ?? 'new';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step 4 — Match existing</CardTitle>
        <p className="text-sm text-muted-foreground">Per family: new or existing. Per product: new or existing.</p>
      </CardHeader>
      <CardContent className="space-y-6">
        {blocks.map((block, blockIdx) => (
          <Card key={blockIdx}>
            <CardHeader>
              <CardTitle>{block.family.family_name}</CardTitle>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Family:</Label>
                <Select
                  value={getFamilyDecision(blockIdx) === 'new' ? '_new' : getFamilyDecision(blockIdx)}
                  onValueChange={(v) => setFamilyDecision(blockIdx, v === '_new' ? 'new' : v)}
                >
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_new">New family</SelectItem>
                    {productFamilies.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.family_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {getFamilyDecision(blockIdx) === 'new' && (
                <div className="mt-2">
                  <Label className="text-xs">Familias mezclables (opcional)</Label>
                  <p className="text-xs text-muted-foreground mb-1">Se pueden combinar en un mismo contenedor en la cotización.</p>
                  <MultiSelect
                    options={productFamilies.map((f) => ({ value: f.id, label: f.family_name }))}
                    value={mixOptionFamilyIdsByBlock[blockIdx] ?? []}
                    onChange={(ids) => setMixOptionFamilyIdsByBlock((prev) => ({ ...prev, [blockIdx]: ids }))}
                    placeholder="Ninguna"
                  />
                </div>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Match</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {block.products.map((prod, rowIdx) => (
                    <TableRow key={rowIdx}>
                      <TableCell>{prod.internal_name || '(sin nombre)'}</TableCell>
                      <TableCell>
                        <Select
                          value={getProductDecision(blockIdx, rowIdx) === 'new' ? '_new' : getProductDecision(blockIdx, rowIdx)}
                          onValueChange={(v) => setProductDecision(blockIdx, rowIdx, v === '_new' ? 'new' : v)}
                        >
                          <SelectTrigger className="w-64">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_new">New product</SelectItem>
                            {sourcingItems.map((i) => (
                              <SelectItem key={i.id} value={i.id}>{i.internal_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))}
      </CardContent>
    </Card>
  );
}

/** Step 3 columns: all editable (text/number/select) + visibility (public = store; private = admin only). Same as MAPPING_TARGET_FIELDS. */
const CONVERTED_COLUMNS: { key: string; label: string; type: 'text' | 'number' | 'select'; visibility: 'public' | 'private' }[] = [
  { key: 'internal_name', label: 'Internal name', type: 'text', visibility: 'public' },
  { key: 'sales_format', label: 'Sales format', type: 'text', visibility: 'public' },
  { key: 'cost_usd', label: 'Cost USD', type: 'number', visibility: 'private' },
  { key: 'units_per_box', label: 'Units/box', type: 'number', visibility: 'public' },
  { key: 'boxes_per_pallet', label: 'Boxes/pallet', type: 'number', visibility: 'public' },
  { key: 'boxes_per_container', label: 'Cajas/contenedor', type: 'number', visibility: 'public' },
  { key: 'gross_weight_per_box_kg', label: 'Weight/box kg', type: 'number', visibility: 'public' },
  { key: 'box_length_cm', label: 'Largo caja (cm)', type: 'number', visibility: 'public' },
  { key: 'box_width_cm', label: 'Ancho caja (cm)', type: 'number', visibility: 'public' },
  { key: 'box_height_cm', label: 'Alto caja (cm)', type: 'number', visibility: 'public' },
  { key: 'moq_value', label: 'MOQ value', type: 'number', visibility: 'public' },
  { key: 'moq_unit_id', label: 'MOQ unit', type: 'select', visibility: 'public' },
  { key: 'observaciones', label: 'Observaciones', type: 'text', visibility: 'public' },
  { key: 'internal_notes', label: 'Info. interna', type: 'text', visibility: 'private' },
  { key: 'cost_incoterm_id', label: 'Cost incoterm', type: 'select', visibility: 'private' },
  { key: 'production_time_days', label: 'Production time (days)', type: 'number', visibility: 'public' },
  { key: 'payment_terms', label: 'Payment terms', type: 'text', visibility: 'private' },
  { key: 'referencia', label: 'Referencia proveedor', type: 'text', visibility: 'private' },
  { key: 'packshot_image', label: 'Link de imagen', type: 'text', visibility: 'public' },
];

function rowKey(blockIdx: number, rowIdx: number): string {
  return `${blockIdx}-${rowIdx}`;
}

function Step3Converted({
  blocks,
  setBlocks,
  moqUnits,
  costIncoterms,
}: {
  blocks: ConvertedBlock[];
  setBlocks: (b: ConvertedBlock[]) => void;
  moqUnits: { id: string; name: string }[];
  costIncoterms: { id: string; name: string }[];
}) {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [bulkField, setBulkField] = useState<string>(CONVERTED_COLUMNS[0].key);
  const [bulkValue, setBulkValue] = useState('');

  const numericKeys = new Set(['cost_usd', 'units_per_box', 'boxes_per_pallet', 'boxes_per_container', 'gross_weight_per_box_kg', 'box_length_cm', 'box_width_cm', 'box_height_cm', 'moq_value', 'production_time_days']);
  const updateFamily = (blockIndex: number, field: string, value: string) => {
    setBlocks(
      blocks.map((blk, bi) => {
        if (bi !== blockIndex) return blk;
        return { ...blk, family: { ...blk.family, [field]: value || undefined } };
      })
    );
  };
  const updateProduct = (blockIndex: number, rowIndex: number, field: string, value: string) => {
    const next = blocks.map((blk, bi) => {
      if (bi !== blockIndex) return blk;
      return {
        ...blk,
        products: blk.products.map((p, ri) => {
          if (ri !== rowIndex) return p;
          const out = { ...p };
          if (numericKeys.has(field)) {
            const n = parseFloat(value.replace(/,/g, '.'));
            (out as Record<string, unknown>)[field] = value === '' || Number.isNaN(n) ? undefined : n;
          } else {
            (out as Record<string, unknown>)[field] = value === '' ? undefined : value;
          }
          return out;
        }),
      };
    });
    setBlocks(next);
  };

  const toggleRow = (blockIdx: number, rowIdx: number) => {
    const key = rowKey(blockIdx, rowIdx);
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleBlockAll = (blockIdx: number, checked: boolean) => {
    const block = blocks[blockIdx];
    if (!block) return;
    setSelectedRows((prev) => {
      const next = new Set(prev);
      block.products.forEach((_, rowIdx) => {
        const k = rowKey(blockIdx, rowIdx);
        if (checked) next.add(k);
        else next.delete(k);
      });
      return next;
    });
  };
  const clearSelection = () => setSelectedRows(new Set());

  const applyBulkToSelected = () => {
    const field = bulkField;
    const value = bulkValue;
    const next = blocks.map((blk, blockIdx) => ({
      ...blk,
      products: blk.products.map((p, rowIdx) => {
        if (!selectedRows.has(rowKey(blockIdx, rowIdx))) return p;
        const out = { ...p };
        if (numericKeys.has(field)) {
          const n = parseFloat(value.replace(/,/g, '.'));
          (out as Record<string, unknown>)[field] = value === '' || Number.isNaN(n) ? undefined : n;
        } else {
          (out as Record<string, unknown>)[field] = value === '' ? undefined : value;
        }
        return out;
      }),
    }));
    setBlocks(next);
  };

  const bulkColumn = CONVERTED_COLUMNS.find((c) => c.key === bulkField);
  const isBulkSelect = bulkColumn?.type === 'select';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step 3 — Converted preview</CardTitle>
        <p className="text-sm text-muted-foreground">Edita cada celda o usa selección masiva: marca filas con la casilla de la izquierda (o «Sel.» en el encabezado para todas) y aplica un valor abajo.</p>
        <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-1">
          <Badge variant="default" className="bg-green-600 hover:bg-green-600 text-white text-[10px] px-1.5 py-0">Público</Badge>
          = visible para clientes
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Interno</Badge>
          = solo control interno
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {blocks.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border bg-muted/50">
            {selectedRows.size > 0 ? (
              <>
                <span className="text-sm font-medium">{selectedRows.size} fila(s) seleccionada(s)</span>
                <Select value={bulkField} onValueChange={setBulkField}>
                  <SelectTrigger className="w-[200px] h-9">
                    <SelectValue placeholder="Columna" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONVERTED_COLUMNS.map((c) => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isBulkSelect && bulkField === 'moq_unit_id' ? (
                  <Select value={bulkValue || '_none'} onValueChange={(v) => setBulkValue(v === '_none' ? '' : v)}>
                    <SelectTrigger className="w-[180px] h-9">
                      <SelectValue placeholder="Valor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">—</SelectItem>
                      {moqUnits.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : isBulkSelect && bulkField === 'cost_incoterm_id' ? (
                  <Select value={bulkValue || '_none'} onValueChange={(v) => setBulkValue(v === '_none' ? '' : v)}>
                    <SelectTrigger className="w-[180px] h-9">
                      <SelectValue placeholder="Valor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">—</SelectItem>
                      {costIncoterms.map((inc) => (
                        <SelectItem key={inc.id} value={inc.id}>{inc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    className="h-9 w-[180px]"
                    type="text"
                    inputMode={bulkColumn?.type === 'number' ? 'decimal' : 'text'}
                    placeholder={bulkColumn?.type === 'number' ? '0' : 'Valor'}
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                  />
                )}
                <Button type="button" size="sm" onClick={applyBulkToSelected}>
                  Aplicar a seleccionados
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={clearSelection}>
                  Desmarcar todas
                </Button>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">Marca filas con la casilla «Sel.» (primera columna) para rellenar varias a la vez.</span>
            )}
          </div>
        )}

        {blocks.length === 0 ? (
          <p className="text-muted-foreground">No converted data. Complete step 2 mapping first.</p>
        ) : (
          blocks.map((block, blockIdx) => (
            <Card key={blockIdx}>
              <CardHeader>
                <CardTitle>{block.family.family_name}</CardTitle>
                <p className="text-xs text-muted-foreground">slug: {block.family.slug}</p>
                <div className="mt-2">
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-2">
                    <Label>Descripción familia (opcional)</Label>
                    <Badge variant="default" className="bg-green-600 hover:bg-green-600 text-white text-[10px] px-1.5 py-0">Público</Badge>
                  </span>
                  <Textarea
                    className="mt-1 min-h-[60px]"
                    placeholder="Marketing description para esta familia..."
                    value={block.family.family_description ?? ''}
                    onChange={(e) => updateFamily(blockIdx, 'family_description', e.target.value)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 align-top whitespace-nowrap">
                        <span className="block font-medium text-xs">Sel.</span>
                        <Checkbox
                          className="mt-1"
                          checked={block.products.length > 0 && block.products.every((_, rowIdx) => selectedRows.has(rowKey(blockIdx, rowIdx)))}
                          onCheckedChange={(checked) => toggleBlockAll(blockIdx, checked === true)}
                          aria-label="Seleccionar todas esta familia"
                        />
                      </TableHead>
                      {CONVERTED_COLUMNS.map((c) => (
                        <TableHead key={c.key} className="align-top">
                          <span className="block font-medium">{c.label}</span>
                          <span className="block mt-0.5">
                            {c.visibility === 'public' ? (
                              <Badge variant="default" className="bg-green-600 hover:bg-green-600 text-white text-[10px] px-1.5 py-0">Público</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Interno</Badge>
                            )}
                          </span>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {block.products.map((prod, rowIdx) => (
                      <TableRow key={rowIdx}>
                        <TableCell className="w-12">
                          <Checkbox
                            checked={selectedRows.has(rowKey(blockIdx, rowIdx))}
                            onCheckedChange={() => toggleRow(blockIdx, rowIdx)}
                            aria-label={`Seleccionar fila ${rowIdx + 1}`}
                          />
                        </TableCell>
                        {CONVERTED_COLUMNS.map((c) => (
                          <TableCell key={c.key}>
                            {c.type === 'select' && c.key === 'moq_unit_id' ? (
                              <Select
                                value={(prod[c.key] as string) ?? '_none'}
                                onValueChange={(v) => updateProduct(blockIdx, rowIdx, c.key, v === '_none' ? '' : v)}
                              >
                                <SelectTrigger className="h-8 max-w-[180px]">
                                  <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">—</SelectItem>
                                  {moqUnits.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : c.type === 'select' && c.key === 'cost_incoterm_id' ? (
                              <Select
                                value={prod.cost_incoterm_id ?? costIncoterms.find((i) => i.name === prod.cost_incoterm)?.id ?? '_none'}
                                onValueChange={(v) => updateProduct(blockIdx, rowIdx, c.key, v === '_none' ? '' : v)}
                              >
                                <SelectTrigger className="h-8 max-w-[180px]">
                                  <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">—</SelectItem>
                                  {costIncoterms.map((inc) => (
                                    <SelectItem key={inc.id} value={inc.id}>{inc.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                className="h-8"
                                type={c.type === 'number' ? 'text' : 'text'}
                                inputMode={c.type === 'number' ? 'decimal' : 'text'}
                                placeholder={c.type === 'number' ? '0' : ''}
                                value={String(prod[c.key] ?? '')}
                                onChange={(e) => updateProduct(blockIdx, rowIdx, c.key, e.target.value)}
                              />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))
        )}
      </CardContent>
    </Card>
  );
}

/** Truncate sample value for display in mapping UI. */
function truncateSample(value: string, maxLen = 40): string {
  const s = String(value).trim();
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen) + '…';
}

function Step2Mapping({
  rawHeaders,
  sampleRow,
  mappingEdit,
  setMappingEdit,
  loading,
  onAcceptAll,
}: {
  rawHeaders: string[];
  /** First row from the first block (AI output) to show sample values per column when mapping. */
  sampleRow: Record<string, string> | null;
  mappingEdit: ColumnMapping | null;
  setMappingEdit: (m: ColumnMapping) => void;
  loading: boolean;
  onAcceptAll: () => void;
}) {
  const updateKey = (key: string, columns: string[]) => {
    setMappingEdit({
      ...(mappingEdit ?? {}),
      [key]: columns,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step 2 — Column mapping</CardTitle>
        <p className="text-sm text-muted-foreground">
          Map provider columns to our schema. Several catalogue columns can be grouped into one of our fields (values are concatenated). family_name comes from the block. The &quot;Ej: …&quot; under each column is the value from the first row so you see what you are mapping.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-sm text-muted-foreground">Loading suggestion…</p>}
        <Button variant="outline" onClick={onAcceptAll} disabled={loading || rawHeaders.length === 0}>
          Accept all (suggest mapping)
        </Button>
        <p className="text-xs text-muted-foreground mb-2">
          <span className="inline-flex items-center gap-2">
            <Badge variant="default" className="bg-green-600 hover:bg-green-600 text-white text-[10px] px-1.5 py-0">Público</Badge>
            = visible para clientes en la tienda
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-2">Interno</Badge>
            = solo control interno
          </span>
        </p>
        <div className="space-y-4">
          {MAPPING_TARGET_FIELDS.map(({ key, label, visibility }) => {
            const selected = mappingEdit?.[key] ?? [];
            const available = rawHeaders.filter((h) => !selected.includes(h));
            return (
              <div key={key} className="flex flex-wrap items-start gap-2 sm:items-center">
                <div className="w-48 shrink-0 pt-2 sm:pt-0 flex flex-col gap-0.5">
                  <Label>{label}</Label>
                  <span className="text-[10px]">
                    {visibility === 'public' ? (
                      <Badge variant="default" className="bg-green-600 hover:bg-green-600 text-white px-1.5 py-0">Público</Badge>
                    ) : (
                      <Badge variant="secondary" className="px-1.5 py-0">Interno</Badge>
                    )}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
                  {selected.map((col, index) =>
                    col.startsWith(MAPPING_LITERAL_PREFIX) ? (
                      <span
                        key={`literal-${key}-${index}`}
                        className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-sm"
                      >
                        <Input
                          className="h-7 w-28 text-sm border-0 bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                          placeholder="Texto libre…"
                          value={col.slice(MAPPING_LITERAL_PREFIX.length)}
                          onChange={(e) => {
                            const next = [...selected];
                            next[index] = MAPPING_LITERAL_PREFIX + e.target.value;
                            updateKey(key, next);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => updateKey(key, selected.filter((_, i) => i !== index))}
                          className="rounded hover:bg-muted-foreground/20 p-0.5 shrink-0"
                          aria-label="Quitar texto libre"
                        >
                          ×
                        </button>
                      </span>
                    ) : (
                      <span
                        key={col}
                        className="inline-flex flex-col gap-0.5 rounded-md bg-muted px-2 py-1 text-sm"
                      >
                        <span className="inline-flex items-center gap-1">
                          {col}
                          <button
                            type="button"
                            onClick={() => updateKey(key, selected.filter((_, i) => i !== index))}
                            className="rounded hover:bg-muted-foreground/20 p-0.5"
                            aria-label={`Quitar ${col}`}
                          >
                            ×
                          </button>
                        </span>
                        {sampleRow?.[col] != null && (
                          <span className="text-[10px] text-muted-foreground" title={sampleRow[col]}>
                            Ej: {truncateSample(sampleRow[col])}
                          </span>
                        )}
                      </span>
                    )
                  )}
                  <Select
                    value="_add"
                    onValueChange={(v) => {
                      if (v && v !== '_add') {
                        if (v === '__literal__') {
                          updateKey(key, [...selected, MAPPING_LITERAL_PREFIX]);
                        } else {
                          updateKey(key, [...selected, v]);
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="max-w-[200px] h-8">
                      <SelectValue placeholder="Añadir columna…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_add">Añadir columna…</SelectItem>
                      <SelectItem value="__literal__">Texto libre…</SelectItem>
                      {available.length === 0 ? (
                        <SelectItem value="_none" disabled>
                          Todas asignadas
                        </SelectItem>
                      ) : (
                        available.map((h) => {
                          const sample = sampleRow?.[h];
                          return (
                            <SelectItem key={h} value={h}>
                              {sample != null ? `${h} — Ej: ${truncateSample(sample, 30)}` : h}
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                  {selected.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-muted-foreground"
                      onClick={() => updateKey(key, [])}
                    >
                      Vaciar
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function PlaceholderStep({ step }: { step: number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-muted-foreground">Step {step} — Content coming in next implementation.</p>
      </CardContent>
    </Card>
  );
}

function DraftList({ onResume }: { onResume: (id: string) => void }) {
  const { data: drafts, isLoading } = useCatalogueConversionDrafts();
  if (isLoading || !drafts?.length) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>My drafts</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1">
          {drafts.slice(0, 5).map((d) => (
            <li key={d.id}>
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-left"
                onClick={() => onResume(d.id)}
              >
                Resume — step {d.step} — {new Date(d.updated_at).toLocaleString()}
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
